<template>
  <RfEditModal
    v-if="props.editItem"
    ref="renameModal"
    :title="`Edit prompt ${props.editItem.name}`"
    @confirm="v => props.onSubmitRename(v)"
    @close="emit('update:editItem', null)"
  />
  <RfBaseModal
    v-else-if="props.deleteItem"
    ref="deleteModal"
    :message="`${deleteDialogText.body} This cannot be undone.`"
    width="580"
    errorText="Delete"
    @error="emit('delete')"
    @close="emit('update:deleteItem', null)"
  >
    <template #title>
      Are you sure you want to delete
      <span class="font-bold whitespace-pre"> {{ deleteDialogText.title }} </span> ?
    </template>
  </RfBaseModal>
  <RfBaseModal
    v-else-if="props.exportItem"
    ref="exportModal"
    message="Select from the following export formats"
    width="580"
    primaryText="Export"
    @secondary="emit('export', exportSelection)"
    @close="emit('update:exportItem', null)"
  >
    <template #title>
      Select the export format for
      <span class="font-bold whitespace-pre"> {{ exportDialogText.title }}</span
      >.
    </template>
    <template #body>
      <RfAlert v-if="exportSelection !== 'summary' && showExportAlert" type="warning" class="mb-4">
        <span> Only <span class="mx-0 px-0 font-bold">Prompt Summary</span> supports filters </span>
      </RfAlert>
      <RfRadioGroup
        v-model="exportSelection"
        vertical
        :options="[
          { value: 'summary', label: 'Prompt Summary' },
          { value: 'path_activities', label: 'Detail' },
          { value: 'experiment_metrics', label: 'Experiment Summary' },
        ]"
      />
    </template>
  </RfBaseModal>
  <RfClonePromptModal
    v-else-if="cloneItem"
    ref="cloneModal"
    :item="cloneItem"
    :isSuperAdmin="props.isSuperAdmin"
    :isCompanyAdmin="props.isCompanyAdmin"
    :currUser="props.currUser"
    :currApp="props.currApp"
    @update:item="v => emit('update:cloneItem', v)"
    @clone="opts => emit('clone', opts)"
    @cloneAsVariation="opts => emit('cloneAsVariation', opts)"
  />
  <RfBaseModal
    v-else-if="statusUpdate.prompt"
    ref="statusModal"
    :title="`${StringUtils.capitalize(statusChangeDialogText.title)} Prompt`"
    width="580"
    :secondaryText="StringUtils.capitalize(statusChangeDialogText.title)"
    @secondary="updateStatus"
    @close="emit('update:statusUpdate', { status: null, prompt: null })"
  >
    <template #body>
      <span v-if="Array.isArray(statusUpdate.prompt)">
        Are you sure you want to {{ statusChangeDialogText.title }}
        <b>prompts({{ statusUpdate.prompt.length }})</b>?
        <b>These prompts({{ statusUpdate.prompt.length }})</b> will
        {{ statusChangeDialogText.body }}
      </span>
      <span v-else>
        Are you sure you want to {{ statusChangeDialogText.title }} this prompt? The prompt will
        {{ statusChangeDialogText.body }}
      </span>
      <div
        v-if="
          isPushNotification(statusUpdate.prompt) &&
          statusUpdate.status === StatusesSimplifiedConstants.running.type &&
          props.statusUpdate.prompt.actions?.rf_pinpoint_campaign_event_triggered === 'false'
        "
      >
        Push notifications will be sent now instead of at the scheduled time.
      </div>
      <RfStartStopDatePicker
        v-if="statusUpdate.status === StatusesSimplifiedConstants.ready.type"
        ref="dateRangePicker"
        class="mt-4"
        :dateRange="{
          startDate: dayjs(statusUpdate.prompt.start_date).utc().toISOString(),
          endDate: dayjs(statusUpdate.prompt.end_date).utc().toISOString(),
        }"
        :model="statusUpdate.prompt"
      />
    </template>
  </RfBaseModal>
</template>

<script setup>
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import RfClonePromptModal from "@/blocks/RfModals/RfClonePromptModal.vue";
import RfEditModal from "@/components/modals/RfEditModal.vue";
import StringUtils from "@/utils/StringUtils";
import {
  SimplifyStatusesMapConstants,
  StatusesSimplifiedConstants,
} from "@/utils/constants/PromptStatusesConstants";
import getPromptStatus from "@/utils/prompts/getPromptStatus";
import dayjs from "dayjs";
import { computed, nextTick, ref, watchEffect } from "vue";
import { returnFutureEndDate } from "@/utils/TimeUtils";
import { isPushNotification } from "@/utils/prompts/promptHelpers";
import RfStartStopDatePicker from "@/components/RfStartStopDatePicker.vue";
import { DEFAULT_END_DATE_DAYJS } from "@/utils/DateDisplayUtils";
import TimezonesConstants from "@/utils/constants/TimezonesConstants";
import RfRadioGroup from "@/components/inputs/RfRadioGroup.vue";
import RfAlert from "@/components/alerts/RfAlert.vue";

const emit = defineEmits([
  "update:editItem",
  "update:deleteItem",
  "update:cloneItem",
  "update:statusUpdate",
  "update:exportItem",
  "update:bulkTagItem",
  "export",
  "clone",
  "cloneAsVariation",
  "delete",
  "status",
  "bulkTag",
]);

const props = defineProps({
  editItem: { type: Object, default: null },
  deleteItem: { type: Array, default: null },
  exportItem: { type: Array, default: null },
  cloneItem: { type: Object, default: null },
  bulkTagItem: { type: [Array, Object], default: null },
  selectedPrompts: { type: Array, default: () => [] },
  availableTags: { type: Array, default: () => [] },
  currApp: { type: Object, default: null },
  currUser: { type: Object, default: null },
  timezone: { type: String, default: null },
  statusUpdate: { type: Object, default: () => ({ status: null, prompt: null }) },
  onSubmitRename: { type: Function, default: () => {} },
  isSuperAdmin: { type: Boolean, default: false },
  isCompanyAdmin: { type: Boolean, default: false },
  showExportAlert: { type: Boolean, default: false },
});

const renameModal = ref();
const deleteModal = ref();
const exportModal = ref();
const cloneModal = ref();
const statusModal = ref();
const bulkTagModal = ref();
const dateRangePicker = ref();
const exportSelection = ref("summary");

const promptStatus = computed(() =>
  !props.statusUpdate.prompt
    ? null
    : getPromptStatus(
        Array.isArray(props.statusUpdate.prompt)
          ? props.statusUpdate.prompt[0]
          : props.statusUpdate.prompt,
        props.timezone,
      ).type,
);

const deleteDialogText = computed(() => {
  if (props.deleteItem?.length > 1)
    return {
      title: `${props.deleteItem.length} prompts`,
      body: `Deleting this prompts(${props.deleteItem.length}) removes all information about these prompts(${props.deleteItem.length}).`,
    };
  return {
    title: props.deleteItem[0].name,
    body: "Deleting this prompt removes all information about this prompt.",
  };
});

const exportDialogText = computed(() => {
  if (props.exportItem?.length > 1)
    return {
      title: `${props.exportItem.length} selected prompts`,
      body: `${props.exportItem.length} prompts will be exported with the selected option`,
    };

  return {
    title: props.exportItem[0].name,
    body: "The prompt will be exported with the selected option.",
  };
});

const simplifiedStatusUpdate = computed(
  () => SimplifyStatusesMapConstants[props.statusUpdate.status],
);

const statusChangeDialogText = computed(() => {
  if (simplifiedStatusUpdate.value.type === StatusesSimplifiedConstants.running.type)
    return {
      title: promptStatus.value === StatusesSimplifiedConstants.paused.type ? "resume" : "start",
      body: "run immediately.",
    };
  if (simplifiedStatusUpdate.value.type === StatusesSimplifiedConstants.paused.type)
    return { title: "pause", body: "stop running immediately." };
  if (simplifiedStatusUpdate.value.type === StatusesSimplifiedConstants.ended.type)
    return { title: "end", body: "stop running immediately." };
  if (simplifiedStatusUpdate.value.type === StatusesSimplifiedConstants.ready.type)
    return { title: "schedule", body: "run on start date." };
  return { title: "", body: "" };
});

const updateStatus = () => {
  const item = {
    ...props.statusUpdate.prompt,
    is_enabled: [
      StatusesSimplifiedConstants.running.type,
      StatusesSimplifiedConstants.ready.type,
    ].includes(simplifiedStatusUpdate.value.type),
  };
  const isPush = isPushNotification(props.statusUpdate.prompt);
  const nowInTimezone = dayjs().tz(TimezonesConstants[props.timezone] || "UTC");

  const runningPausedSwitch =
    (simplifiedStatusUpdate.value.type === StatusesSimplifiedConstants.running.type &&
      promptStatus.value !== StatusesSimplifiedConstants.paused.type) ||
    (simplifiedStatusUpdate.value.type === StatusesSimplifiedConstants.paused.type &&
      promptStatus.value === StatusesSimplifiedConstants.ended.type);
  if (runningPausedSwitch) {
    item.start_date = isPush
      ? nowInTimezone.format("YYYY-MM-DDTHH:mm:ss[Z]")
      : nowInTimezone.format("YYYY-MM-DD");
    if (!isPush) item.end_date = DEFAULT_END_DATE_DAYJS.format("YYYY-MM-DD");
    item.is_expired = false;
  }

  if (simplifiedStatusUpdate.value.type === StatusesSimplifiedConstants.ended.type) {
    item.end_date = isPush
      ? nowInTimezone.format("YYYY-MM-DDTHH:mm:ss[Z]")
      : nowInTimezone.subtract(1, "days").format("YYYY-MM-DD");
    if (dayjs(item.end_date).isBefore(item.start_date)) {
      item.start_date = item.end_date;
    }
    item.is_expired = true;
  }

  if (simplifiedStatusUpdate.value.type === StatusesSimplifiedConstants.ready.type) {
    if (dateRangePicker.value) {
      const { fullStartDate, fullEndDate, frequency, localTime } = dateRangePicker.value;
      if (dayjs(fullStartDate) > dayjs(fullEndDate))
        throw new Error("Start date must be before end date");

      item.start_date = fullStartDate;
      item.end_date = fullEndDate;
      if (frequency) item.actions.rf_push_frequency = frequency;
      if (localTime) item.actions.rf_push_use_local_time = localTime;
    }
  }

  const prompts = Array.isArray(props.statusUpdate.prompt)
    ? props.statusUpdate.prompt.map(({ id, end_date }) => ({
        ...item,
        id,
        end_date: runningPausedSwitch
          ? returnFutureEndDate(end_date, item.end_date)
          : item.end_date,
      }))
    : [
        {
          ...item,
          id: props.statusUpdate.prompt.id,
          end_date: runningPausedSwitch
            ? returnFutureEndDate(props.statusUpdate.prompt.end_date, item.end_date)
            : item.end_date,
        },
      ];

  emit("status", prompts);
};

const closeDeleteModal = () => deleteModal.value?.close();
const closeCloneModal = () => cloneModal.value?.close();
const closeExportModal = () => exportModal.value?.close();
const closeStatusModal = () => statusModal.value?.close();
const closeBulkTagModal = () => bulkTagModal.value?.reset();

watchEffect(() => {
  if (props.editItem)
    nextTick(() =>
      renameModal.value?.open({
        name: props.editItem.name,
        description: props.editItem.description,
      }),
    );
  else if (props.deleteItem) nextTick(() => deleteModal.value?.show());
  else if (props.exportItem) nextTick(() => exportModal.value?.show());
  else if (props.cloneItem) nextTick(() => cloneModal.value?.show());
  else if (props.statusUpdate.prompt) nextTick(() => statusModal.value?.show());
  else if (props.bulkTagItem) nextTick(() => bulkTagModal.value?.show?.());
});

defineExpose({
  closeDeleteModal,
  closeCloneModal,
  closeExportModal,
  closeStatusModal,
  closeBulkTagModal,
});
</script>
